import { LazyCompassGoLiveModal } from "@circle-react/components/LiveStreams/CompassGoLiveModal/LazyCompassGoLiveModal";
import { useLiveStreamsUsageLimits } from "@circle-react/components/LiveStreams/hooks";
import { useCanCreateEvent } from "@circle-react/components/LiveStreams/LiveFormV3/Modal/hooks/useCanCreateEvent";
import { useAddSpaceModal } from "@circle-react/components/Modals/AddSpaceModal";
import { useQuickPostModal } from "@circle-react/components/QuickPost";
import { useShouldDisplayGoLiveFooter } from "@circle-react/components/Spaces/SidebarV2/SidebarFooter/useShouldDisplayGoLiveFooter";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { isCommunityAdmin } from "@circle-react/helpers/communityMemberHelpers";
import { useCanCreatePost } from "@circle-react/hooks/posts/useCanCreatePost";
import { Drawer } from "@circleco/compass/components/Drawer";
import { useState } from "react";

import { t } from "@/i18n-js/instance";
import { useCreateEvent } from "@/react/components/Events/NewEventButton/useCreateEvent";
import { useCanCreateSpace } from "@/react/components/Mobile/components/useCanShowMobileCreateButton";

import { DrawerMenuItem } from "./components/DrawerMenuItem";

interface MobileCreateDrawerProps {
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

export const MobileCreateDrawer = ({
  open,
  onOpenChange,
}: MobileCreateDrawerProps) => {
  const [isGoLiveOpen, setIsGoLiveOpen] = useState(false);
  const postModal = useQuickPostModal();
  const spaceModal = useAddSpaceModal();
  const canCreateSpace = useCanCreateSpace();
  const { canCreatePost } = useCanCreatePost();
  const { canCreateEvent } = useCanCreateEvent();
  const { showCreateEventModal } = useCreateEvent();
  const hasGoLiveButton = useShouldDisplayGoLiveFooter();

  const {
    isPending: areLiveLimitsLoading,
    isMonthlyHourLimitReached,
    isStorageLimitReached,
  } = useLiveStreamsUsageLimits({ enabled: hasGoLiveButton });
  const { currentCommunityMember, isViewOnlyMasquerading } =
    usePunditUserContext();
  const areLiveLimitsReached =
    isMonthlyHourLimitReached || isStorageLimitReached;
  const shouldDisableGoLive =
    isViewOnlyMasquerading ||
    (!isCommunityAdmin(currentCommunityMember) &&
      (areLiveLimitsLoading || areLiveLimitsReached));

  const handleCreatePost = () => {
    onOpenChange(false);
    void postModal.show();
  };

  const handleCreateEvent = () => {
    onOpenChange(false);
    void showCreateEventModal();
  };

  const handleCreateSpace = () => {
    onOpenChange(false);
    void spaceModal.show();
  };

  const handleGoLive = () => {
    onOpenChange(false);
    setIsGoLiveOpen(true);
  };

  return (
    <>
      <Drawer.Root open={open} onOpenChange={onOpenChange}>
        <Drawer.Content aria-label={t("mobile_shell.create")}>
          <div className="flex flex-col">
            {canCreatePost && (
              <DrawerMenuItem
                icon="page-edit"
                label={t("mobile_shell.create_post")}
                onClick={handleCreatePost}
              />
            )}
            {canCreateEvent && (
              <DrawerMenuItem
                icon="calendar-add"
                label={t("events.new_event")}
                onClick={handleCreateEvent}
              />
            )}
            {canCreateSpace && (
              <DrawerMenuItem
                icon="plus"
                label={t("add_space")}
                onClick={handleCreateSpace}
              />
            )}
            {hasGoLiveButton && (
              <DrawerMenuItem
                icon="live"
                label={t("sidebar_footer_v3.go_live")}
                onClick={handleGoLive}
                disabled={shouldDisableGoLive ?? false}
              />
            )}
          </div>
        </Drawer.Content>
      </Drawer.Root>

      {/* Rendered outside Drawer.Root so base-ui doesn't suppress its backdrop */}
      {hasGoLiveButton && (
        <LazyCompassGoLiveModal
          open={isGoLiveOpen}
          onOpenChange={setIsGoLiveOpen}
          disabled={shouldDisableGoLive}
        />
      )}
    </>
  );
};
